import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, PhoneIcon, ShieldCheckIcon, FileTextIcon, BellIcon, GlobeIcon, MoonIcon, CameraIcon, CheckCircle2Icon, CheckIcon, ChevronRightIcon, HeartIcon, ImageIcon, TagIcon, StarIcon, HelpCircleIcon, LockIcon, LogOutIcon } from 'lucide-react';
const fade = (i: number) => ({
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    delay: i * 0.06,
    duration: 0.35
  }
});
function Segmented({
  options,
  value,
  onChange,
  groupId
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  groupId: string;
}) {
  return <div className="flex bg-slate-100 rounded-xl p-1 gap-1" data-id="element-2825">
      {options.map(opt => {
      const active = opt === value;
      return <button key={opt} onClick={() => onChange(opt)} className="relative flex-1 py-1.5 text-[13px] font-medium rounded-lg transition-colors" data-id="element-2826">
            {active && <motion.span layoutId={`seg-active-${groupId}`} transition={{
          type: 'spring',
          stiffness: 500,
          damping: 35
        }} className="absolute inset-0 bg-white rounded-lg shadow-sm" data-id="element-2827" />}
            <span className={`relative z-10 ${active ? 'text-dark-navy font-semibold' : 'text-slate-500'}`} data-id="element-2828">
              {opt}
            </span>
          </button>;
    })}
    </div>;
}
type ContactField = 'email' | 'phone';
export function Profile() {
  const [notifications, setNotifications] = useState(true);
  const [language, setLanguage] = useState('English');
  const [theme, setTheme] = useState('Light');
  const [photoSheet, setPhotoSheet] = useState(false);
  const [uaePassConnected, setUaePassConnected] = useState(false);
  const [idScan, setIdScan] = useState({
    front: false,
    back: false
  });
  const idVerified = idScan.front && idScan.back;
  const [profile, setProfile] = useState({
    name: 'Ahmed Al Mansoori',
    email: 'ahmed@example.com',
    phone: '+971 50 123 4567'
  });
  const [verified, setVerified] = useState({
    email: true,
    phone: true
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(profile);
  const [otpQueue, setOtpQueue] = useState<ContactField[]>([]);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState(false);
  const activeOtp = otpQueue[0] ?? null;
  const startEdit = () => {
    setDraft(profile);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft(profile);
  };
  const saveEdit = () => {
    const changed: ContactField[] = [];
    if (draft.email.trim() !== profile.email) changed.push('email');
    if (draft.phone.trim() !== profile.phone) changed.push('phone');
    setProfile(p => ({
      ...p,
      name: draft.name.trim() || p.name
    }));
    if (changed.length === 0) {
      setEditing(false);
      return;
    }
    setVerified(v => {
      const next = {
        ...v
      };
      changed.forEach(f => next[f] = false);
      return next;
    });
    setOtpCode('');
    setOtpError(false);
    setOtpQueue(changed);
  };
  const verifyOtp = () => {
    if (otpCode.length < 4 || !activeOtp) {
      setOtpError(true);
      return;
    }
    setProfile(p => ({
      ...p,
      [activeOtp]: draft[activeOtp].trim()
    }));
    setVerified(v => ({
      ...v,
      [activeOtp]: true
    }));
    const rest = otpQueue.slice(1);
    setOtpQueue(rest);
    setOtpCode('');
    setOtpError(false);
    if (rest.length === 0) setEditing(false);
  };
  const cancelOtp = () => {
    setOtpQueue([]);
    setOtpCode('');
    setOtpError(false);
  };
  const otpTarget = activeOtp === 'email' ? draft.email.trim() : activeOtp === 'phone' ? draft.phone.trim() : '';
  const stats = [{
    label: 'Listings',
    value: '6',
    icon: TagIcon
  }, {
    label: 'Saved',
    value: '24',
    icon: HeartIcon
  }, {
    label: 'Trust',
    value: '4.9',
    icon: StarIcon
  }];
  return <div className="flex-1 w-full overflow-y-auto bg-slate-50 pb-12 relative" data-id="element-2829">
      {/* Hero */}
      <motion.div {...fade(0)} className="bg-dark-navy px-5 pt-8 pb-16 relative" data-id="element-2830">
        <div className="flex items-center gap-4" data-id="element-2831">
          <div className="relative flex-shrink-0" data-id="element-2832">
            <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=160&h=160&q=80" alt="Ahmed Al Mansoori" className="w-[72px] h-[72px] rounded-full object-cover ring-4 ring-white/10" data-id="element-2833" />
            <button onClick={() => setPhotoSheet(true)} aria-label="Change photo" className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary-indigo text-white flex items-center justify-center shadow-md border-2 border-dark-navy active:scale-90 transition-transform" data-id="element-2834">
              <CameraIcon className="w-3.5 h-3.5" data-id="element-2835" />
            </button>
          </div>
          <div className="min-w-0 flex-1" data-id="element-2836">
            <h1 className="font-display font-bold text-xl text-white truncate" data-id="element-2837">
              {profile.name}
            </h1>
            <p className="text-white/50 text-sm" data-id="element-2838">Member since 2024</p>
            {idVerified ? <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-success/15 text-success text-[11px] font-bold rounded-full" data-id="element-2839">
                <ShieldCheckIcon className="w-3 h-3" data-id="element-2840" /> Verified Account
              </span> : <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-white/10 text-white/60 text-[11px] font-bold rounded-full" data-id="element-2841">
                Unverified · Scan your ID
              </span>}
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="px-4 -mt-9 relative z-10" data-id="element-2842">
        <motion.div {...fade(1)} className="bg-white rounded-2xl border border-slate-200 shadow-sm grid grid-cols-3 divide-x divide-slate-100" data-id="element-2843">
          {stats.map(s => <div key={s.label} className="flex flex-col items-center py-3.5" data-id="element-2844">
              <s.icon className="w-4 h-4 text-primary-indigo mb-1" data-id="element-2845" />
              <span className="font-display font-bold text-lg text-dark-navy leading-none" data-id="element-2846">
                {s.value}
              </span>
              <span className="text-[11px] text-slate-400 mt-0.5" data-id="element-2847">
                {s.label}
              </span>
            </div>)}
        </motion.div>
      </div>

      <div className="px-4 mt-5 space-y-5" data-id="element-2848">
        {/* Account details */}
        <motion.section {...fade(2)} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-2849">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100" data-id="element-2850">
            <h2 className="font-semibold text-sm text-dark-navy" data-id="element-2851">
              Account details
            </h2>
            <button onClick={startEdit} className="text-xs font-semibold text-primary-indigo" data-id="element-2852">
              Edit
            </button>
          </div>

          <div className="divide-y divide-slate-100" data-id="element-2853">
            <div className="flex items-center gap-3 px-4 py-3" data-id="element-2854">
              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2855">
                <MailIcon className="w-[18px] h-[18px] text-slate-500" data-id="element-2856" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-2857">
                <p className="text-[11px] text-slate-400" data-id="element-2858">Email</p>
                <p className="text-sm font-medium text-slate-900 truncate" data-id="element-2859">
                  {profile.email}
                </p>
              </div>
              {verified.email ? <ShieldCheckIcon className="w-4 h-4 text-success flex-shrink-0" data-id="element-2860" /> : <span className="text-[11px] font-semibold text-amber-600 flex-shrink-0" data-id="element-2861">
                  Unverified
                </span>}
            </div>

            <div className="flex items-center gap-3 px-4 py-3" data-id="element-2862">
              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2863">
                <PhoneIcon className="w-[18px] h-[18px] text-slate-500" data-id="element-2864" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-2865">
                <p className="text-[11px] text-slate-400" data-id="element-2866">Phone</p>
                <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5" data-id="element-2867">
                  <img src="https://flagcdn.com/w40/ae.png" alt="" className="w-5 h-[14px] rounded-[2px] object-cover ring-1 ring-slate-200" data-id="element-2868" />
                  {profile.phone}
                </p>
              </div>
              {verified.phone ? <ShieldCheckIcon className="w-4 h-4 text-success flex-shrink-0" data-id="element-2869" /> : <span className="text-[11px] font-semibold text-amber-600 flex-shrink-0" data-id="element-2870">
                  Unverified
                </span>}
            </div>
          </div>

          {/* UAE Pass */}
          {uaePassConnected ? <div className="m-3 mt-1 flex items-center gap-3 p-3 rounded-xl bg-success/[0.07] border border-success/20" data-id="element-2871">
              <div className="relative flex-shrink-0" data-id="element-2872">
                <div className="w-9 h-9 bg-black rounded-lg flex items-center justify-center text-white font-bold text-[10px]" data-id="element-2873">
                  UAE
                </div>
                <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-success border-2 border-white flex items-center justify-center" data-id="element-2874">
                  <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-2875" />
                </span>
              </div>
              <div className="min-w-0 flex-1" data-id="element-2876">
                <p className="text-sm font-semibold text-slate-900 flex items-center gap-1" data-id="element-2877">
                  UAE Pass
                  <span className="text-[11px] font-bold text-success" data-id="element-2878">
                    · Connected
                  </span>
                </p>
                <p className="text-[11px] text-slate-500 leading-tight" data-id="element-2879">
                  Identity verified · Trust score boosted
                </p>
              </div>
              <button onClick={() => setUaePassConnected(false)} className="text-[11px] font-semibold text-slate-400 active:text-slate-600 flex-shrink-0" data-id="element-2880">
                Disconnect
              </button>
            </div> : <div className="m-3 mt-1 flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-dashed border-slate-300" data-id="element-2881">
              <div className="w-9 h-9 bg-black rounded-lg flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0" data-id="element-2882">
                UAE
              </div>
              <div className="min-w-0 flex-1" data-id="element-2883">
                <p className="text-sm font-semibold text-slate-900" data-id="element-2884">UAE Pass</p>
                <p className="text-[11px] text-slate-500 leading-tight" data-id="element-2885">
                  Verify identity for a higher trust score
                </p>
              </div>
              <button onClick={() => setUaePassConnected(true)} className="px-3.5 py-1.5 bg-black text-white rounded-lg font-semibold text-xs active:scale-95 transition-transform flex-shrink-0" data-id="element-2886">
                Connect
              </button>
            </div>}
        </motion.section>

        {/* Documents */}
        <motion.section {...fade(3)} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-id="element-2887">
          <div className="flex items-center justify-between mb-3" data-id="element-2888">
            <h2 className="font-semibold text-sm text-dark-navy" data-id="element-2889">
              ID verification
            </h2>
            {idVerified ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-[11px] font-bold rounded-full" data-id="element-2890">
                <ShieldCheckIcon className="w-3 h-3" data-id="element-2891" /> Verified
              </span> : <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 text-[11px] font-bold rounded-full" data-id="element-2892">
                Pending
              </span>}
          </div>
          <p className="text-[12px] text-slate-500 mb-3 leading-relaxed" data-id="element-2893">
            Scan the front and back of your Emirates ID to verify your account.
          </p>
          <div className="grid grid-cols-2 gap-2.5" data-id="element-2894">
            {[{
            side: 'front' as const,
            label: 'Front'
          }, {
            side: 'back' as const,
            label: 'Back'
          }].map(d => {
            const done = idScan[d.side];
            return <button key={d.side} onClick={() => setIdScan(prev => ({
              ...prev,
              [d.side]: true
            }))} className={`flex flex-col items-center justify-center gap-2 py-5 px-1 rounded-2xl border transition-all active:scale-[0.97] ${done ? 'bg-success/5 border-success/40' : 'bg-slate-50 border-dashed border-slate-300 text-slate-400 hover:text-primary-indigo'}`} data-id="element-2895">
                  {done ? <CheckCircle2Icon className="w-6 h-6 text-success" data-id="element-2896" /> : <CameraIcon className="w-6 h-6" data-id="element-2897" />}
                  <span className={`text-[12px] font-semibold text-center leading-tight ${done ? 'text-success' : 'text-slate-600'}`} data-id="element-2898">
                    {done ? `${d.label} scanned` : `Scan ${d.label}`}
                  </span>
                </button>;
          })}
          </div>
          {idVerified && <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-success/5 border border-success/20 rounded-xl" data-id="element-2899">
              <ShieldCheckIcon className="w-4 h-4 text-success flex-shrink-0" data-id="element-2900" />
              <span className="text-[12px] font-medium text-success" data-id="element-2901">
                Your identity is verified — your account now shows the verified
                badge.
              </span>
            </div>}

          <h2 className="font-semibold text-sm text-dark-navy mt-5 mb-3" data-id="element-2902">
            My documents
          </h2>
          <button className="w-full flex items-center justify-center gap-2 py-4 px-1 bg-slate-50 rounded-2xl border border-dashed border-slate-300 active:scale-[0.97] transition-transform text-slate-400 hover:text-primary-indigo" data-id="element-2903">
            <FileTextIcon className="w-5 h-5" data-id="element-2904" />
            <span className="text-[12px] font-medium text-slate-600" data-id="element-2905">
              Upload CV
            </span>
          </button>
        </motion.section>

        {/* Preferences */}
        <motion.section {...fade(4)} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-2906">
          <h2 className="font-semibold text-sm text-dark-navy px-4 pt-3 pb-1" data-id="element-2907">
            Preferences
          </h2>
          <div className="divide-y divide-slate-100" data-id="element-2908">
            <div className="flex items-center gap-3 px-4 py-3" data-id="element-2909">
              <BellIcon className="w-[18px] h-[18px] text-slate-500 flex-shrink-0" data-id="element-2910" />
              <span className="flex-1 text-sm font-medium text-slate-700" data-id="element-2911">
                Notifications
              </span>
              <button onClick={() => setNotifications(v => !v)} role="switch" aria-checked={notifications} aria-label="Toggle notifications" className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${notifications ? 'bg-primary-indigo' : 'bg-slate-300'}`} data-id="element-2912">
                <motion.span layout transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow ${notifications ? 'left-[22px]' : 'left-0.5'}`} data-id="element-2913" />
              </button>
            </div>

            <div className="px-4 py-3" data-id="element-2914">
              <div className="flex items-center gap-3 mb-2.5" data-id="element-2915">
                <GlobeIcon className="w-[18px] h-[18px] text-slate-500 flex-shrink-0" data-id="element-2916" />
                <span className="flex-1 text-sm font-medium text-slate-700" data-id="element-2917">
                  Language
                </span>
              </div>
              <Segmented groupId="language" options={['English', 'العربية']} value={language} onChange={setLanguage} data-id="element-2918" />
            </div>

            <div className="px-4 py-3" data-id="element-2919">
              <div className="flex items-center gap-3 mb-2.5" data-id="element-2920">
                <MoonIcon className="w-[18px] h-[18px] text-slate-500 flex-shrink-0" data-id="element-2921" />
                <span className="flex-1 text-sm font-medium text-slate-700" data-id="element-2922">
                  Theme
                </span>
              </div>
              <Segmented groupId="theme" options={['Light', 'Dark', 'System']} value={theme} onChange={setTheme} data-id="element-2923" />
            </div>
          </div>
        </motion.section>

        {/* Account actions */}
        <motion.section {...fade(5)} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100" data-id="element-2924">
          {[{
          icon: HelpCircleIcon,
          label: 'Help & support'
        }, {
          icon: LockIcon,
          label: 'Privacy & security'
        }].map(row => <button key={row.label} className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-slate-50 transition-colors" data-id="element-2925">
              <row.icon className="w-[18px] h-[18px] text-slate-500 flex-shrink-0" data-id="element-2926" />
              <span className="flex-1 text-left text-sm font-medium text-slate-700" data-id="element-2927">
                {row.label}
              </span>
              <ChevronRightIcon className="w-4 h-4 text-slate-300 flex-shrink-0" data-id="element-2928" />
            </button>)}
          <button className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-rose-50 transition-colors" data-id="element-2929">
            <LogOutIcon className="w-[18px] h-[18px] text-rose-500 flex-shrink-0" data-id="element-2930" />
            <span className="flex-1 text-left text-sm font-semibold text-rose-500" data-id="element-2931">
              Sign out
            </span>
          </button>
        </motion.section>

        <p className="text-center text-[11px] text-slate-400 pt-1" data-id="element-2932">
          Talab · Version 1.0.0
        </p>
      </div>

      {/* Full-screen edit profile */}
      <AnimatePresence data-id="element-2933">
        {editing && <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 340,
        damping: 34
      }} className="absolute inset-0 z-30 bg-slate-50 flex flex-col" data-id="element-2934">
            <div className="flex-shrink-0 flex items-center justify-between px-2 py-3 bg-white border-b border-slate-100" data-id="element-2935">
              <button onClick={cancelEdit} className="px-2 py-1 text-sm font-medium text-slate-500" data-id="element-2936">
                Cancel
              </button>
              <h2 className="font-semibold text-dark-navy" data-id="element-2937">Edit profile</h2>
              <span className="w-[52px]" data-id="element-2938" />
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-5" data-id="element-2939">
              <div className="flex flex-col items-center mb-6" data-id="element-2940">
                <div className="relative" data-id="element-2941">
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=160&h=160&q=80" alt="" className="w-20 h-20 rounded-full object-cover ring-4 ring-white shadow-sm" data-id="element-2942" />
                  <button onClick={() => setPhotoSheet(true)} className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary-indigo text-white flex items-center justify-center shadow-md border-2 border-slate-50 active:scale-90 transition-transform" data-id="element-2943">
                    <CameraIcon className="w-3.5 h-3.5" data-id="element-2944" />
                  </button>
                </div>
                <button onClick={() => setPhotoSheet(true)} className="mt-2 text-xs font-medium text-primary-indigo" data-id="element-2945">
                  Change photo
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100" data-id="element-2946">
                <div className="px-4 py-3" data-id="element-2947">
                  <label className="text-[11px] font-medium text-slate-400" data-id="element-2948">
                    Full name
                  </label>
                  <input value={draft.name} onChange={e => setDraft(d => ({
                ...d,
                name: e.target.value
              }))} className="mt-1 w-full text-[15px] font-medium text-slate-900 bg-transparent outline-none" data-id="element-2949" />
                </div>
                <div className="px-4 py-3" data-id="element-2950">
                  <div className="flex items-center justify-between" data-id="element-2951">
                    <label className="text-[11px] font-medium text-slate-400" data-id="element-2952">
                      Email
                    </label>
                    {draft.email.trim() !== profile.email && <span className="text-[10px] font-semibold text-amber-600" data-id="element-2953">
                        Will require OTP
                      </span>}
                  </div>
                  <input type="email" value={draft.email} onChange={e => setDraft(d => ({
                ...d,
                email: e.target.value
              }))} className="mt-1 w-full text-[15px] font-medium text-slate-900 bg-transparent outline-none" data-id="element-2954" />
                </div>
                <div className="px-4 py-3" data-id="element-2955">
                  <div className="flex items-center justify-between" data-id="element-2956">
                    <label className="text-[11px] font-medium text-slate-400" data-id="element-2957">
                      Phone
                    </label>
                    {draft.phone.trim() !== profile.phone && <span className="text-[10px] font-semibold text-amber-600" data-id="element-2958">
                        Will require OTP
                      </span>}
                  </div>
                  <input type="tel" value={draft.phone} onChange={e => setDraft(d => ({
                ...d,
                phone: e.target.value
              }))} className="mt-1 w-full text-[15px] font-medium text-slate-900 bg-transparent outline-none" data-id="element-2959" />
                </div>
              </div>

              <p className="flex items-start gap-1.5 text-[11px] text-slate-400 mt-3 px-1" data-id="element-2960">
                <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" data-id="element-2961" />
                For your security, changing your email or phone requires a
                one-time code sent to the new contact.
              </p>
            </div>

            <div className="flex-shrink-0 px-4 pt-3 pb-7 bg-white border-t border-slate-100" data-id="element-2962">
              <button onClick={saveEdit} className="w-full py-3.5 bg-primary-indigo text-white rounded-2xl font-semibold active:scale-[0.98] transition-transform shadow-[0_8px_24px_rgb(67,56,202,0.35)]" data-id="element-2963">
                Save changes
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Photo source action sheet */}
      <AnimatePresence data-id="element-2964">
        {photoSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setPhotoSheet(false)} className="absolute inset-0 bg-black/40 z-[55]" data-id="element-2965" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 360,
          damping: 34
        }} className="absolute inset-x-0 bottom-0 z-[60] px-3 pb-6" data-id="element-2966">
              <div className="bg-white rounded-2xl overflow-hidden mb-2 divide-y divide-slate-100" data-id="element-2967">
                <p className="text-center text-[12px] text-slate-400 py-3" data-id="element-2968">
                  Update profile photo
                </p>
                <button onClick={() => setPhotoSheet(false)} className="w-full flex items-center justify-center gap-2.5 py-3.5 text-[15px] font-medium text-primary-indigo active:bg-slate-50 transition-colors" data-id="element-2969">
                  <CameraIcon className="w-5 h-5" data-id="element-2970" />
                  Take photo
                </button>
                <button onClick={() => setPhotoSheet(false)} className="w-full flex items-center justify-center gap-2.5 py-3.5 text-[15px] font-medium text-primary-indigo active:bg-slate-50 transition-colors" data-id="element-2971">
                  <ImageIcon className="w-5 h-5" data-id="element-2972" />
                  Choose from gallery
                </button>
              </div>
              <button onClick={() => setPhotoSheet(false)} className="w-full py-3.5 bg-white rounded-2xl text-[15px] font-semibold text-slate-600 active:bg-slate-50 transition-colors" data-id="element-2973">
                Cancel
              </button>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* OTP verification sheet */}
      <AnimatePresence data-id="element-2974">
        {activeOtp && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={cancelOtp} className="absolute inset-0 bg-black/40 z-40" data-id="element-2975" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 360,
          damping: 34
        }} className="absolute inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl px-5 pt-3 pb-8" data-id="element-2976">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" data-id="element-2977" />
              <div className="w-12 h-12 rounded-2xl bg-primary-indigo/10 flex items-center justify-center mx-auto mb-3" data-id="element-2978">
                {activeOtp === 'email' ? <MailIcon className="w-6 h-6 text-primary-indigo" data-id="element-2979" /> : <PhoneIcon className="w-6 h-6 text-primary-indigo" data-id="element-2980" />}
              </div>
              <h3 className="font-display font-bold text-lg text-dark-navy text-center" data-id="element-2981">
                Verify your {activeOtp}
              </h3>
              <p className="text-sm text-slate-500 text-center mt-1 mb-5 px-4" data-id="element-2982">
                We sent a 4-digit code to{' '}
                <span className="font-medium text-slate-700" data-id="element-2983">{otpTarget}</span>.
                {otpQueue.length > 1 && <span className="block text-[11px] text-slate-400 mt-1" data-id="element-2984">
                    Step 1 of {otpQueue.length}
                  </span>}
              </p>

              <input inputMode="numeric" maxLength={4} value={otpCode} autoFocus onChange={e => {
            setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4));
            setOtpError(false);
          }} placeholder="••••" className={`w-full text-center tracking-[0.6em] text-2xl font-bold py-3 bg-slate-50 border rounded-2xl outline-none transition-colors ${otpError ? 'border-rose-400' : 'border-slate-200 focus:border-primary-indigo/50'}`} data-id="element-2985" />
              {otpError && <p className="text-xs text-rose-500 text-center mt-2" data-id="element-2986">
                  Enter the 4-digit code to continue.
                </p>}

              <button onClick={verifyOtp} className="w-full mt-5 py-3.5 bg-primary-indigo text-white rounded-2xl font-semibold active:scale-[0.98] transition-transform" data-id="element-2987">
                Verify & continue
              </button>
              <button onClick={cancelOtp} className="w-full mt-2 py-2.5 text-sm font-medium text-slate-400" data-id="element-2988">
                Resend code · Cancel
              </button>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}